import { Voucher } from "@/lib/accounting/types";
import { renderUPISection } from "./UPIPaymentSection";

interface CompanyInfo {
  name: string;
  address: string;
  gstin: string;
  state: string;
  phone: string;
  email: string;
  pan?: string;
  bankName?: string;
  bankAccount?: string;
  bankIFSC?: string;
  bankBranch?: string;
}

/**
 * GST Standard template — matches traditional Indian GST Tax Invoice format
 * with Billed-to / Shipped-to, transport details, bank info, and terms.
 */
export function renderGSTStandardTemplate(
  company: CompanyInfo,
  voucher: Voucher,
  docTitle: string,
  invoiceDate: string,
  taxableAmount: number,
  amountInWords: string
): string {
  const c = company;
  const v = voucher;

  const isSales = v.type === "Sales" || v.type === "Credit Note";

  // Extract item lines (exclude tax and party lines)
  const itemLines = v.lines
    .filter(l => !l.ledgerName.includes("CGST") && !l.ledgerName.includes("SGST") && !l.ledgerName.includes("IGST"))
    .filter(l => l.ledgerName !== v.partyName);

  const hasRealItems = Array.isArray(v.items) && v.items.length > 0;
  const displayItems = hasRealItems
    ? v.items!
    : itemLines.map(l => ({
        name: l.narration || l.ledgerName,
        description: l.narration || l.ledgerName,
        hsnCode: "—",
        qty: 1,
        unit: "Nos",
        rate: isSales ? l.credit : l.debit,
        amount: isSales ? l.credit : l.debit,
        gstRate: v.gstAmount ? 18 : 0
      }));

  let runningTotal = 0;
  const itemRows = displayItems.map((item, i) => {
    const amt = Number(item.amount) || ((Number(item.qty) || 1) * (Number(item.rate) || 0)) || 0;
    runningTotal += amt;
    return `
      <tr>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-size:12px">${i + 1}</td>
        <td style="border:1px solid #000;padding:5px 8px;font-size:12px"><strong>${item.name || item.description}</strong>${item.description && item.description !== item.name ? `<br><span style="font-size:10px;color:#555">${item.description}</span>` : ''}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-size:12px">${item.hsnCode || "—"}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-size:12px">${item.gstRate !== undefined ? `${item.gstRate}%` : "—"}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:right;font-size:12px">${item.qty || 1}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-size:12px">${item.unit || "Pcs"}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:right;font-size:12px">${(Number(item.rate) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:right;font-size:12px">${amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
      </tr>`;
  }).join("");

  // Empty filler rows for the traditional look
  const fillerCount = Math.max(0, 5 - displayItems.length);
  const fillerRows = Array(fillerCount).fill(0).map(() => `
    <tr>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px">&nbsp;</td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
      <td style="border:1px solid #000;padding:5px 8px;font-size:12px"></td>
    </tr>`).join("");

  const pan = c.gstin ? c.gstin.substring(2, 12) : "";
  const stateCode = c.gstin ? c.gstin.substring(0, 2) : "";

  return `
  <div style="border:2px solid #000;font-family:'Segoe UI',Tahoma,sans-serif;color:#000;background:#fff">
    
    <!-- ═══ HEADER ═══ -->
    <div style="border-bottom:2px solid #000;padding:0">
      <div style="display:flex;border-bottom:1px solid #000">
        <div style="flex:1;padding:8px 12px;border-right:1px solid #000;font-size:11px">
          <strong>GSTIN :</strong> ${c.gstin}
        </div>
        <div style="padding:8px 12px;text-align:center;font-weight:bold;font-size:12px;min-width:180px;border-right:1px solid #000">
          Form GST INV-1
        </div>
        <div style="flex:1;padding:8px 12px;text-align:right;font-size:11px">
          <strong>Tel :</strong> ${c.phone}<br>
          <strong>Email :</strong> ${c.email}
        </div>
      </div>
      <div style="display:flex;border-bottom:1px solid #000">
        <div style="flex:1;padding:4px 12px;font-size:11px">
          <strong>PAN :</strong> ${pan}
        </div>
        <div style="flex:2;text-align:center;padding:10px">
          <h1 style="font-size:20px;font-weight:bold;margin:0;color:#000">${c.name}</h1>
          <p style="font-size:11px;margin:4px 0 0;color:#333">${c.address}</p>
        </div>
        <div style="flex:1;padding:4px 12px;text-align:right;font-size:11px">
          ${v.qrCodeData ? `<img src="${v.qrCodeData}" style="width:65px;height:65px;border:1px solid #000;padding:2px;background:#fff;display:inline-block" alt="GST QR Code"/>` : '&nbsp;'}
        </div>
      </div>
    </div>

    <!-- ═══ TAX INVOICE TITLE ═══ -->
    <div style="text-align:center;border-bottom:2px solid #000;padding:6px 0;background:#f5f5f5">
      <h2 style="margin:0;font-size:16px;font-weight:bold;letter-spacing:3px;text-transform:uppercase">${docTitle}</h2>
    </div>

    <!-- ═══ INVOICE META ═══ -->
    <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:2px solid #000;font-size:11px">
      <div style="border-right:1px solid #000">
        <div style="display:flex;border-bottom:1px solid #000">
          <div style="flex:1;padding:4px 10px;border-right:1px solid #000"><strong>Invoice No.</strong> ${v.invoiceNumber || v.voucherNumber}</div>
          <div style="flex:1;padding:4px 10px"><strong>Reverse Charge :</strong> No</div>
        </div>
        <div style="padding:4px 10px;border-bottom:1px solid #000">
          <strong>ORIGINAL FOR RECIPIENT</strong>
          ${v.irn ? `<br><strong style="font-size:9px">IRN:</strong> <span style="font-family:monospace;font-size:8px;word-break:break-all">${v.irn}</span>` : ''}
          ${v.ackNo ? `<br><span style="font-size:9px"><strong>Ack No:</strong> ${v.ackNo} | <strong>Ack Dt:</strong> ${v.ackDate ? new Date(v.ackDate).toLocaleDateString('en-IN') : invoiceDate}</span>` : ''}
        </div>
      </div>
      <div>
        <div style="display:flex;border-bottom:1px solid #000">
          <div style="flex:1;padding:4px 10px;border-right:1px solid #000"><strong>Invoice Dt.</strong> ${invoiceDate}</div>
          <div style="flex:1;padding:4px 10px"><strong>Payment Mode :</strong> CREDIT</div>
        </div>
        <div style="display:flex;border-bottom:1px solid #000">
          <div style="flex:1;padding:4px 10px;border-right:1px solid #000"><strong>Vehicle No.</strong> —</div>
          <div style="flex:1;padding:4px 10px"><strong>E-Way Bill No.</strong> ${v.ewayBillNo || "—"}</div>
        </div>
        <div style="display:flex">
          <div style="flex:1;padding:4px 10px;border-right:1px solid #000"><strong>Place of Supply :</strong> ${c.state}${stateCode ? '-' + stateCode : ''}</div>
          <div style="flex:1;padding:4px 10px"><strong>Transport :</strong> —</div>
        </div>
      </div>
    </div>

    <!-- ═══ BILLED TO / SHIPPED TO ═══ -->
    <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:2px solid #000;font-size:11px">
      <div style="border-right:1px solid #000;padding:8px 10px">
        <div style="background:#eee;padding:4px 8px;margin-bottom:6px;font-weight:bold;font-size:11px;border:1px solid #ccc">
          Details of Receiver ( Billed to )
        </div>
        <p style="margin:2px 0"><strong>Name :</strong> ${v.partyName || "—"}</p>
        <p style="margin:2px 0"><strong>Address :</strong> —</p>
        <p style="margin:2px 0"><strong>State :</strong> ${c.state}${stateCode ? '-' + stateCode : ''}</p>
        <p style="margin:2px 0"><strong>GSTIN :</strong> —</p>
        <p style="margin:2px 0"><strong>PAN :</strong> —</p>
      </div>
      <div style="padding:8px 10px">
        <div style="background:#eee;padding:4px 8px;margin-bottom:6px;font-weight:bold;font-size:11px;border:1px solid #ccc">
          Details of Consignee ( Shipped to )
        </div>
        <p style="margin:2px 0">${v.partyName || "—"}</p>
        <p style="margin:2px 0">—</p>
        <p style="margin:2px 0">${c.state}${stateCode ? '-' + stateCode : ''}</p>
        <p style="margin:2px 0"><strong>GSTIN :</strong> —</p>
        <p style="margin:2px 0"><strong>PAN :</strong> —</p>
      </div>
    </div>

    <!-- ═══ ITEMS TABLE ═══ -->
    <div style="border-bottom:2px solid #000">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f0f0f0">
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:35px;text-align:center">Sr</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;text-align:left">Description of Goods</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:75px;text-align:center">HSN</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:45px;text-align:center">Pcs</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:65px;text-align:right">Qty.</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:45px;text-align:center">Unit</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:85px;text-align:right">Rate</th>
            <th style="border:1px solid #000;padding:6px 4px;font-size:11px;font-weight:bold;width:95px;text-align:right">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
          ${fillerRows}
        </tbody>
      </table>
    </div>

    <!-- ═══ TOTALS & TAX ═══ -->
    <div style="border-bottom:2px solid #000">
      <table style="width:100%;border-collapse:collapse;font-size:12px">
        <tr>
          <td style="border:1px solid #000;padding:5px 10px;width:60%;font-weight:bold">Total</td>
          <td style="border:1px solid #000;padding:5px 10px;text-align:right;width:20%">${itemLines.length > 0 ? itemLines.reduce((s, _) => s + 1, 0) : '—'}</td>
          <td style="border:1px solid #000;padding:5px 10px;text-align:right;width:20%;font-weight:bold">${taxableAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>
        ${v.cgst ? `<tr>
          <td colspan="2" style="border:1px solid #000;padding:4px 10px;text-align:right">Add : CGST</td>
          <td style="border:1px solid #000;padding:4px 10px;text-align:right">₹${v.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>` : ""}
        ${v.sgst ? `<tr>
          <td colspan="2" style="border:1px solid #000;padding:4px 10px;text-align:right">Add : SGST</td>
          <td style="border:1px solid #000;padding:4px 10px;text-align:right">₹${v.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>` : ""}
        ${v.igst ? `<tr>
          <td colspan="2" style="border:1px solid #000;padding:4px 10px;text-align:right">Add : IGST</td>
          <td style="border:1px solid #000;padding:4px 10px;text-align:right">₹${v.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>` : ""}
        <tr style="background:#f5f5f5">
          <td colspan="2" style="border:1px solid #000;padding:6px 10px;font-weight:bold;font-size:13px">Grand Total</td>
          <td style="border:1px solid #000;padding:6px 10px;text-align:right;font-weight:bold;font-size:14px">₹${v.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
        </tr>
      </table>
    </div>

    <!-- ═══ AMOUNT IN WORDS ═══ -->
    <div style="border-bottom:2px solid #000;padding:8px 12px;font-size:12px">
      <strong>Rs.(Inwords) :</strong> ${amountInWords}
    </div>

    <!-- ═══ BANK & TERMS ═══ -->
    <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #000;font-size:11px">
      <div style="border-right:1px solid #000;padding:10px 12px">
        <p style="font-weight:bold;margin-bottom:4px;text-decoration:underline">Our Bank :</p>
        <p style="margin:2px 0">${c.bankName || "—"}</p>
        <p style="margin:2px 0">A/C NO. : ${c.bankAccount || "—"}</p>
        <p style="margin:2px 0">IFSC CODE : ${c.bankIFSC || "—"}</p>
        <p style="margin:2px 0">${c.bankBranch || ""}</p>
      </div>
      <div style="padding:10px 12px">
        <p style="font-weight:bold;margin-bottom:4px;text-decoration:underline">Terms & Conditions :</p>
        <p style="margin:2px 0;font-size:10px">1. Our responsibility ceases after the goods are removed from our premises.</p>
        <p style="margin:2px 0;font-size:10px">2. Goods once sold are not returnable or exchangeable.</p>
        <p style="margin:2px 0;font-size:10px">3. If the bill is not paid within a week, Interest @24% will be charged from the date of bill.</p>
        <p style="margin:8px 0 0;font-size:10px;font-style:italic">Subject to ${c.state} Jurisdiction Only</p>
      </div>
    </div>

    ${(v.type === "Sales" || v.type === "Debit Note") ? renderUPISection(v.totalDebit, v.invoiceNumber || v.voucherNumber) : ""}

    <!-- ═══ FOOTER ═══ -->
    <div style="display:flex;justify-content:space-between;align-items:flex-end;padding:12px 16px;font-size:11px">
      <div>
        <p style="color:#666">E.& O.E.</p>
        <p style="margin-top:24px;color:#666">Checked/Prepared by</p>
      </div>
      <div style="text-align:right">
        <p style="font-weight:bold;margin-bottom:2px">For ${c.name}</p>
        <div style="width:150px;border-bottom:1px solid #999;margin:30px 0 4px auto;height:1px"></div>
        <p style="color:#666">Authorised Signatory</p>
      </div>
    </div>

    <div style="text-align:center;padding:4px;font-size:9px;color:#999;border-top:1px solid #ddd">
      Computer generated invoice
    </div>
  </div>`;
}
